import React, { useState, useEffect } from 'react';
import { FiX, FiMap, FiTarget, FiPenTool, FiCalendar, FiDownload } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useBusinessBrain } from '../../context/BusinessBrainContext';
import { useWorkspace } from '../../context/WorkspaceContext';
import { supabase } from '../../supabase';
import { formatDateTime } from '../../utils/formatters';
import useActiveTab from '../../hooks/useActiveTab';

const MarketingPlanModal = ({ isOpen, onClose }) => {
  const { brainData } = useBusinessBrain();
  const { activeWorkspace } = useWorkspace();
  const [activeTab, setActiveTab] = useActiveTab('marketingPlanModal', 'summary');
  const [campaigns, setCampaigns] = useState([]);
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && activeWorkspace) {
      fetchData();
    }
  }, [isOpen, activeWorkspace]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch Active Campaigns
      const { data: campaignsData } = await supabase
        .from('campaigns')
        .select('*')
        .eq('workspace_id', activeWorkspace.id)
        .eq('status', 'active')
        .limit(5);

      setCampaigns(campaignsData || []);

      // Fetch Upcoming Content
      const { data: contentData } = await supabase
        .from('social_media_posts')
        .select('id, content, scheduled_at, platform, status')
        .eq('workspace_id', activeWorkspace.id)
        .gte('scheduled_at', new Date().toISOString())
        .order('scheduled_at', { ascending: true })
        .limit(5);

      // Fetch Upcoming Emails
      const { data: emailData, error: emailError } = await supabase
          .from('email_campaigns')
          .select('id, name, subject, scheduled_at, status')
          .eq('workspace_id', activeWorkspace.id)
          .gte('scheduled_at', new Date().toISOString())
          .order('scheduled_at', { ascending: true })
          .limit(5);

      if (emailError && !emailError.message.includes('relation "email_campaigns" does not exist')) {
          console.error("Error fetching emails for plan", emailError);
      }

      // Combine and Sort
      const combinedContent = [
          ...(contentData || []).map(c => {
              let textContent = '';
              if (typeof c.content === 'string') {
                  textContent = c.content;
              } else if (c.content && typeof c.content === 'object') {
                  textContent = c.content.text || c.content.caption || JSON.stringify(c.content);
              }
              return {
                  ...c,
                  title: textContent ? (textContent.length > 40 ? textContent.substring(0, 40) + '...' : textContent) : 'Untitled Post',
                  type: 'social'
              };
          }),
          ...(emailData || []).map(e => ({
              id: e.id,
              title: e.name, // Map name to title
              scheduled_at: e.scheduled_at,
              platform: 'Email', // Custom label
              type: 'email',
              status: e.status
          }))
      ].sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at))
       .slice(0, 7); // Show top 7 mixed

      setContent(combinedContent);
    } catch (error) {
      console.error("Error fetching marketing plan data", error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const strategy = brainData?.strategy || {};
  const brand = brainData?.brand || {};

  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
      <div className="bg-white dark:bg-charcoal-900 w-full max-w-5xl max-h-[90vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-charcoal-200 dark:border-charcoal-800">

        {/* Header */}
        <div className="shrink-0 flex items-center justify-between px-6 py-4 border-b border-charcoal-100 dark:border-charcoal-800 bg-white dark:bg-charcoal-900">
            <div className="flex items-center space-x-3">
                <div className="p-2 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg text-white">
                    <SafeIcon icon={FiMap} className="text-xl" />
                </div>
                <div>
                    <h2 className="text-xl font-bold text-charcoal-900 dark:text-white">Marketing Master Plan</h2>
                    <p className="text-sm text-charcoal-500 dark:text-charcoal-400">Your consolidated strategy and execution roadmap</p>
                </div>
            </div>
            <div className="flex items-center space-x-2">
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-charcoal-100 dark:hover:bg-charcoal-800 rounded-lg transition-colors text-charcoal-500"
                >
                    <SafeIcon icon={FiX} className="text-xl" />
                </button>
            </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-charcoal-100 dark:border-charcoal-800 px-6 bg-charcoal-50 dark:bg-charcoal-950/50">
            {['summary', 'strategy', 'execution'].map((tab) => (
                <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors capitalize ${
                        activeTab === tab
                        ? 'border-purple-600 text-purple-600 dark:text-purple-400'
                        : 'border-transparent text-charcoal-600 dark:text-charcoal-400 hover:text-charcoal-900 dark:hover:text-charcoal-200'
                    }`}
                >
                    {tab}
                </button>
            ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 bg-charcoal-50 dark:bg-charcoal-950 min-h-0">
            {activeTab === 'summary' && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Brand Snapshot */}
                    <div className="bg-white dark:bg-charcoal-800 p-6 rounded-xl border border-charcoal-200 dark:border-charcoal-700 shadow-sm">
                        <h3 className="font-semibold text-charcoal-900 dark:text-white mb-4 flex items-center gap-2">
                            <SafeIcon icon={FiTarget} className="text-purple-500" />
                            Brand Identity
                        </h3>
                        <div className="space-y-3">
                            <div>
                                <div className="text-xs text-charcoal-500 uppercase tracking-wide">Mission</div>
                                <p className="text-sm text-charcoal-800 dark:text-charcoal-200 line-clamp-3">
                                    {brand.mission || 'No mission statement defined yet.'}
                                </p>
                            </div>
                            <div>
                                <div className="text-xs text-charcoal-500 uppercase tracking-wide">Voice</div>
                                <p className="text-sm text-charcoal-800 dark:text-charcoal-200">
                                    {brand.brandVoice || 'Not defined'}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Active Campaigns */}
                    <div className="bg-white dark:bg-charcoal-800 p-6 rounded-xl border border-charcoal-200 dark:border-charcoal-700 shadow-sm">
                        <h3 className="font-semibold text-charcoal-900 dark:text-white mb-4 flex items-center gap-2">
                            <SafeIcon icon={FiMap} className="text-blue-500" />
                            Active Campaigns
                        </h3>
                        {loading ? (
                             <div className="text-sm text-charcoal-500">Loading...</div>
                        ) : campaigns.length > 0 ? (
                            <ul className="space-y-3">
                                {campaigns.map(c => (
                                    <li key={c.id} className="flex justify-between items-center text-sm">
                                        <span className="font-medium text-charcoal-700 dark:text-charcoal-300 truncate max-w-[70%]">{c.name}</span>
                                        <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full">Active</span>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <div className="text-sm text-charcoal-500 italic">No active campaigns running.</div>
                        )}
                    </div>

                    {/* Upcoming Content */}
                    <div className="bg-white dark:bg-charcoal-800 p-6 rounded-xl border border-charcoal-200 dark:border-charcoal-700 shadow-sm">
                        <h3 className="font-semibold text-charcoal-900 dark:text-white mb-4 flex items-center gap-2">
                            <SafeIcon icon={FiCalendar} className="text-orange-500" />
                            Upcoming Content
                        </h3>
                         {loading ? (
                             <div className="text-sm text-charcoal-500">Loading...</div>
                        ) : content.length > 0 ? (
                            <ul className="space-y-3">
                                {content.map(c => (
                                    <li key={c.id} className="flex items-center gap-3 text-sm">
                                        <div className={`w-8 h-8 rounded flex items-center justify-center text-xs font-bold ${
                                            c.type === 'email'
                                                ? 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400'
                                                : 'bg-charcoal-100 text-charcoal-500 dark:bg-charcoal-700 dark:text-charcoal-400'
                                        }`}>
                                            {new Date(c.scheduled_at).getDate()}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-medium text-charcoal-700 dark:text-charcoal-300 truncate">{c.title || 'Untitled Post'}</div>
                                            <div className="text-xs text-charcoal-500 flex items-center gap-1">
                                                {c.type === 'email' && <SafeIcon icon={FiTarget} className="text-[10px]" />}
                                                {c.platform}
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <div className="text-sm text-charcoal-500 italic">No content scheduled.</div>
                        )}
                    </div>
                </div>
            )}

            {activeTab === 'strategy' && (
                <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Core Identity */}
                        <div className="bg-white dark:bg-charcoal-800 p-6 rounded-xl border border-charcoal-200 dark:border-charcoal-700 shadow-sm">
                            <h3 className="font-semibold text-charcoal-900 dark:text-white mb-4 flex items-center gap-2">
                                <SafeIcon icon={FiTarget} className="text-purple-500" />
                                Core Identity
                            </h3>
                            <div className="space-y-4">
                                <div>
                                    <label className="text-xs font-bold text-charcoal-500 uppercase tracking-wide">Mission</label>
                                    <p className="text-sm text-charcoal-800 dark:text-charcoal-200 mt-1">{brand.mission || 'Not defined'}</p>
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-charcoal-500 uppercase tracking-wide">Elevator Pitch</label>
                                    <p className="text-sm text-charcoal-800 dark:text-charcoal-200 mt-1">{brand.elevatorPitch || 'Not defined'}</p>
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-charcoal-500 uppercase tracking-wide">Core Values</label>
                                    <p className="text-sm text-charcoal-800 dark:text-charcoal-200 mt-1">{brand.values || 'Not defined'}</p>
                                </div>
                            </div>
                        </div>

                        {/* Audience */}
                        <div className="bg-white dark:bg-charcoal-800 p-6 rounded-xl border border-charcoal-200 dark:border-charcoal-700 shadow-sm">
                            <h3 className="font-semibold text-charcoal-900 dark:text-white mb-4 flex items-center gap-2">
                                <SafeIcon icon={FiTarget} className="text-blue-500" />
                                Target Audience
                            </h3>
                            <p className="text-sm text-charcoal-800 dark:text-charcoal-200 whitespace-pre-wrap">
                                {brand.targetAudience || 'Target audience not fully defined.'}
                            </p>
                        </div>
                    </div>

                    {/* Strategic Notes */}
                    <div className="bg-white dark:bg-charcoal-800 p-6 rounded-xl border border-charcoal-200 dark:border-charcoal-700 shadow-sm">
                         <h3 className="font-semibold text-charcoal-900 dark:text-white mb-4 flex items-center gap-2">
                             <SafeIcon icon={FiPenTool} className="text-yellow-500" />
                             Strategic Notes
                         </h3>
                         <div className="mt-2">
                             {strategy.notes ? (
                                 <div className="bg-charcoal-50 dark:bg-charcoal-900 p-4 rounded-lg border border-charcoal-200 dark:border-charcoal-700">
                                     <pre className="whitespace-pre-wrap font-sans text-sm text-charcoal-700 dark:text-charcoal-300">{strategy.notes}</pre>
                                 </div>
                             ) : (
                                 <p className="text-charcoal-500 italic">No strategic notes found. Go to the Strategy page to add notes.</p>
                             )}
                         </div>
                    </div>
                </div>
            )}

             {activeTab === 'execution' && (
                <div className="space-y-6">
                    <div className="bg-white dark:bg-charcoal-800 p-6 rounded-xl border border-charcoal-200 dark:border-charcoal-700 shadow-sm">
                        <h3 className="font-semibold text-charcoal-900 dark:text-white mb-2">Campaign Performance</h3>
                        <p className="text-sm text-charcoal-500 mb-4">High-level view of your marketing performance.</p>
                        {/* Placeholder for analytics summary */}
                        <div className="h-48 bg-charcoal-50 dark:bg-charcoal-700/50 rounded-lg flex items-center justify-center text-charcoal-400">
                            Analytics Summary Widget Placeholder
                        </div>
                    </div>
                </div>
            )}
        </div>

      </div>
    </div>
  );
};

export default MarketingPlanModal;
